import React, { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import classNames from "classnames";

import { ReducerState } from "reducers";
import { Fa } from "composants/Icon";
import { toggleAppFavoris } from "api";
import { galaxyMenuFavorisSuccess } from "actions/galaxies.action";
import { t } from "utils/i18n";

import "./IconRail.css";

// Debounce du survol : évite d'ouvrir le rail en frôlant le bord gauche, et de le refermer sur un bref écart de la souris
const HOVER_OPEN_DELAY = 150;
const HOVER_CLOSE_DELAY = 200;

const IconRail: FC = () => {
  const favoris = useSelector((state: ReducerState) => state.app.favoris);
  const dispatch = useDispatch();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  function cancelHover() {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = undefined;
  }

  function setOpenAfter(value: boolean, delay: number) {
    cancelHover();
    hoverTimerRef.current = setTimeout(() => setOpen(value), delay);
  }

  // Clic, clavier, bouton fermer : action immédiate, on annule un éventuel survol en attente
  function setOpenNow(value: boolean) {
    cancelHover();
    setOpen(value);
  }

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  function removeFavoris(e: React.MouseEvent, code: string) {
    e.preventDefault();
    e.stopPropagation();
    toggleAppFavoris(code, null).then((res) => {
      dispatch(galaxyMenuFavorisSuccess(res.data));
    });
  }

  // Tactile : pas de mouseleave -> un tap en dehors du panneau referme le rail
  useEffect(() => {
    if (!open) return;

    function handleOutsideClick(e: MouseEvent | TouchEvent) {
      const target = e.target as Node;
      // la poignée est un sibling du panneau, il faut l'exclure sinon le tap d'ouverture referme aussitôt
      const insideRail = railRef.current?.contains(target) ?? false;
      const insideTrigger = triggerRef.current?.contains(target) ?? false;
      if (!insideRail && !insideTrigger) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [open]);

  return (
    <>
      {/* Poignée fine sur le bord gauche : survol (souris) ou tap (tablette) ouvre le rail */}
      <div
        ref={triggerRef}
        className="icon-rail-trigger"
        onMouseEnter={() => setOpenAfter(true, HOVER_OPEN_DELAY)}
        onMouseLeave={() => {
          // simple passage sur la poignée sans s'arrêter : on n'ouvre pas
          if (!open) cancelHover();
        }}
        onClick={() => setOpenNow(true)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpenNow(true);
          }
        }}
      />
      <div
        ref={railRef}
        className={classNames("icon-rail", open && "is-open")}
        onMouseEnter={cancelHover}
        onMouseLeave={() => setOpenAfter(false, HOVER_CLOSE_DELAY)}
      >
        <div className="icon-rail-header">
          <div className="icon-rail-title">{t("commun_favoris", { defaultValue: "Favoris" })}</div>
          {favoris.length > 0 && <span className="icon-rail-count">{favoris.length}</span>}
          <button type="button" className="icon-rail-close" onClick={() => setOpenNow(false)}>
            <Fa icon={["fal", "times"]} fixedWidth />
          </button>
        </div>
        <div className="icon-rail-separator" />
        {favoris.length === 0 && (
          <div className="icon-rail-empty">
            <Fa icon={["fal", "star"]} fixedWidth />
            <span className="icon-rail-empty-text">
              {t("commun_favoris_menu_vide", { defaultValue: "Aucun favori" })}
            </span>
          </div>
        )}
        {favoris.map((fav) => {
          const base = `/page/${fav.code}`;
          const isActive = location.pathname === base || location.pathname.startsWith(`${base}/`);
          return (
            <div key={fav.code} className={classNames("icon-rail-row", isActive && "is-active")}>
              <NavLink
                to={`/page/${fav.code}${fav.link ? fav.link : ""}`}
                className={classNames("icon-rail-btn", isActive && "is-active")}
                title={fav.label}
              >
                <Fa
                  icon={[isActive ? "fas" : "fal", "star"]}
                  className="icon-rail-star"
                  fixedWidth
                />
                <span className="icon-rail-label">{fav.label}</span>
              </NavLink>
              <div className="icon-rail-action">
                <span className="icon-rail-chevron">
                  <Fa icon={["fal", "chevron-right"]} fixedWidth />
                </span>
                <button
                  type="button"
                  className="icon-rail-remove"
                  onClick={(e) => removeFavoris(e, fav.code)}
                >
                  <Fa icon={["fal", "times"]} fixedWidth />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default IconRail;
